import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

export default function NotificationBell({ collapsed = false }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [open,          setOpen]          = useState(false);
  const [enabled,       setEnabled]       = useState(true);
  const [filter,        setFilter]        = useState('all');
  const panelRef = useRef(null);

  const unread = notifications.filter(n => !n.isRead).length;

  const filteredNotifications = notifications.filter(n => {
    if (filter === 'scripts') return n.entityType === 'script';
    if (filter === 'videos')  return ['live_production', 'ai_production', 'video_asset', 'video_comment'].includes(n.entityType);
    return true;
  });

  const fetchNotifications = useCallback(async () => {
    try {
      const [notifRes, prefRes] = await Promise.all([
        api.get('/api/notifications'),
        api.get('/api/notifications/preferences'),
      ]);
      setNotifications(notifRes.data);
      setEnabled(prefRes.data.notificationsEnabled);
    } catch {}
  }, []);

  // Initial load + poll every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close panel on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  async function handleMarkAllRead() {
    try {
      await api.patch('/api/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch {}
  }

  async function handleMarkRead(id) {
    try {
      await api.patch(`/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch {}
  }

  async function handleToggleEnabled() {
    const newVal = !enabled;
    setEnabled(newVal);
    try {
      await api.patch('/api/notifications/preferences', { notificationsEnabled: newVal });
    } catch {
      setEnabled(!newVal); // revert on failure
    }
  }

  async function handleNotifClick(n) {
    if (!n.isRead) handleMarkRead(n.id);
    if (!n.entityType || !n.entityId) return;
    setOpen(false);
    if (n.entityType === 'script') {
      navigate('/scripts', { state: { openScriptId: n.entityId } });
    } else if (n.entityType === 'live_production') {
      navigate(`/production/${n.entityId}`);
    } else if (n.entityType === 'ai_production') {
      navigate('/ai', { state: { openRecordId: n.entityId } });
    } else if (n.entityType === 'video_comment') {
      try {
        const res = await api.get(`/api/comments/single/${n.entityId}`);
        navigate(`/review/${res.data.videoAssetId}?comment=${n.entityId}`);
      } catch { /* silently skip if lookup fails */ }
    } else if (n.entityType === 'video_asset') {
      // legacy notifications stored videoAssetId directly
      navigate(`/review/${n.entityId}`);
    }
  }

  function formatTime(iso) {
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    if (diff < 60000) return 'just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  return (
    <div className="notif-bell-wrap" ref={panelRef}>
      <button
        className={collapsed ? undefined : 'notif-bell-btn'}
        onClick={() => setOpen(v => !v)}
        title="Notifications"
        style={collapsed ? { background: 'none', border: 'none', cursor: 'pointer', position: 'relative', padding: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' } : undefined}
      >
        <span style={{ fontSize: 15 }}>🔔</span>
        {!collapsed && <span>Notifications</span>}
        {unread > 0 && (
          <span className="notif-badge">{unread > 9 ? '9+' : unread}</span>
        )}
      </button>

      {open && (
        <div className="notif-panel">
          <div className="notif-panel-head">
            <span className="notif-panel-title">Notifications</span>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {unread > 0 && (
                <button className="notif-read-all" onClick={handleMarkAllRead}>
                  Mark all read
                </button>
              )}
              <button
                className={`notif-toggle ${enabled ? 'on' : 'off'}`}
                onClick={handleToggleEnabled}
                title={enabled ? 'Disable notifications' : 'Enable notifications'}
              >
                {enabled ? 'On' : 'Off'}
              </button>
            </div>
          </div>

          <div className="notif-filter-bar">
            {[['all', 'All'], ['scripts', 'Scripts'], ['videos', 'Videos']].map(([val, label]) => (
              <button
                key={val}
                className={`notif-filter-btn${filter === val ? ' active' : ''}`}
                onClick={() => setFilter(val)}
              >{label}</button>
            ))}
          </div>

          <div className="notif-list">
            {filteredNotifications.length === 0 ? (
              <div className="notif-empty">No notifications</div>
            ) : (
              filteredNotifications.map(n => (
                <div
                  key={n.id}
                  className={`notif-item${n.isRead ? '' : ' unread'}${n.entityType ? ' has-link' : ''}`}
                  onClick={() => handleNotifClick(n)}
                >
                  <div className="notif-item-body">
                    <div className="notif-item-title">{n.title}</div>
                    <div className="notif-item-text">{n.body}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <div className="notif-item-time">{formatTime(n.createdAt)}</div>
                    {n.entityType && (
                      <span style={{ fontSize: 10, color: 'var(--violet)', fontWeight: 600 }}>View →</span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
